import { useCallback, useEffect, useState } from 'react'
import {
  AgxButton,
  AgxColumn,
  AgxDatePicker,
  AgxEmptyState,
  AgxRow,
} from '@urbanx/agx.ui-components2'
import { SubPagePanel } from 'components/panel/SubPagePanel'
import {
  CoolingOffDetails,
  CoolingOffDetailsCoolingOffType,
} from '@urbanx/agx.campaigns.client'
import dayjs from 'dayjs'
import LoadingState from 'components/loadingState/LoadingState'
import { useSelectedCampaign } from 'hooks'
import { useBusinessDate } from 'hooks/useBusinessDate'

interface CoolingOffPeriodPanelProps {
  contractDate?: Date
  coolingOffDetails: CoolingOffDetails
  onChange: (updatedCoolingOffDetails: CoolingOffDetails) => void
  onDelete: () => void
  hideEditButton?: boolean
  hideDeleteButton?: boolean
}

export function CoolingOffPeriodPanel({
  contractDate,
  coolingOffDetails,
  onChange,
  onDelete,
  hideEditButton,
  hideDeleteButton,
}: CoolingOffPeriodPanelProps) {
  const { campaign } = useSelectedCampaign()
  const { moveToNextBusinessDay, addBusinessDays, isNonBusinessDay } =
    useBusinessDate(campaign?.state || '')
  const [editCoolingOffEndDate, setEditCoolingOffEndDate] = useState(false)

  const calculateCoolingOffEndDate = useCallback(() => {
    if (!contractDate) return undefined

    if (
      coolingOffDetails?.coolingOffType ===
      CoolingOffDetailsCoolingOffType.FiveDays
    ) {
      return addBusinessDays(dayjs(contractDate), 5).toDate()
    }
    if (
      coolingOffDetails?.coolingOffType ===
      CoolingOffDetailsCoolingOffType.TenDays
    ) {
      return addBusinessDays(dayjs(contractDate), 10).toDate()
    }
    if (
      coolingOffDetails?.coolingOffType ===
        CoolingOffDetailsCoolingOffType.NDays &&
      coolingOffDetails?.coolingOffDays
    ) {
      return addBusinessDays(
        dayjs(contractDate),
        coolingOffDetails.coolingOffDays
      ).toDate()
    }
    return undefined
  }, [coolingOffDetails, contractDate, addBusinessDays])

  const [coolingOffEndDate, setCoolingOffEndDate] = useState<Date | undefined>(
    calculateCoolingOffEndDate()
  )

  useEffect(() => {
    setCoolingOffEndDate(calculateCoolingOffEndDate())
  }, [calculateCoolingOffEndDate])

  const countBusinessDaysBetween = useCallback(
    (startDate: Date, endDate: Date): number => {
      let count = 0
      let current = dayjs(startDate)
      const end = dayjs(endDate)

      while (current.isBefore(end, 'day')) {
        current = current.add(1, 'day')
        if (!isNonBusinessDay(current)) {
          count++
        }
      }
      return count
    },
    [isNonBusinessDay]
  )

  const saveCoolingOffEndDate = useCallback(() => {
    if (!editCoolingOffEndDate) {
      setEditCoolingOffEndDate(true)
      return
    }

    if (!coolingOffEndDate || !contractDate) return

    const businessDaysDiff = countBusinessDaysBetween(
      contractDate,
      coolingOffEndDate
    )

    if (businessDaysDiff === 10) {
      onChange({
        coolingOffType: CoolingOffDetailsCoolingOffType.TenDays,
        coolingOffDays: undefined,
        noCoolingOffReason: undefined,
      })
    } else if (businessDaysDiff === 5) {
      onChange({
        coolingOffType: CoolingOffDetailsCoolingOffType.FiveDays,
        coolingOffDays: undefined,
        noCoolingOffReason: undefined,
      })
    } else if (businessDaysDiff > 0) {
      onChange({
        coolingOffType: CoolingOffDetailsCoolingOffType.NDays,
        coolingOffDays: businessDaysDiff,
        noCoolingOffReason: undefined,
      })
    } else {
      onChange({
        coolingOffType: CoolingOffDetailsCoolingOffType.NoCoolingOff,
        coolingOffDays: undefined,
        noCoolingOffReason: undefined,
      })
    }

    setEditCoolingOffEndDate(false)
  }, [
    contractDate,
    coolingOffEndDate,
    editCoolingOffEndDate,
    onChange,
    countBusinessDaysBetween,
  ])

  if (!coolingOffDetails) {
    return <LoadingState />
  }

  return (
    <SubPagePanel
      key="cooling-off-period-panel"
      expandable
      panelTitle="Cooling Off Period"
      defaultExpanded
    >
      <AgxRow
        gap="spacing-md"
        fill
        justifyContent={
          coolingOffDetails?.coolingOffType ===
          CoolingOffDetailsCoolingOffType.NoCoolingOff
            ? 'center'
            : undefined
        }
        alignItems={
          coolingOffDetails?.coolingOffType !==
          CoolingOffDetailsCoolingOffType.NoCoolingOff
            ? 'flex-end'
            : undefined
        }
        padding={{
          top:
            coolingOffDetails?.coolingOffType !==
            CoolingOffDetailsCoolingOffType.NoCoolingOff
              ? 'spacing-md'
              : 'spacing-3xl',
          bottom:
            coolingOffDetails?.coolingOffType !==
            CoolingOffDetailsCoolingOffType.NoCoolingOff
              ? 'spacing-3xl'
              : undefined,
        }}
      >
        {coolingOffDetails?.coolingOffType ===
        CoolingOffDetailsCoolingOffType.NoCoolingOff ? (
          <AgxEmptyState variant="minimal" label="There is no Cooling Off" />
        ) : (
          <>
            <AgxDatePicker
              id="cooling-off-period-due-date"
              stretch
              disabled={!editCoolingOffEndDate}
              label="Due Date"
              value={
                coolingOffEndDate
                  ? moveToNextBusinessDay(dayjs(coolingOffEndDate)).toDate()
                  : undefined
              }
              optionalText=""
              minDate={contractDate ? dayjs(contractDate).toDate() : undefined}
              onChange={(value) => {
                setCoolingOffEndDate(
                  value
                    ? moveToNextBusinessDay(dayjs(value)).toDate()
                    : undefined
                )
              }}
            />
            {!hideEditButton && (
              <AgxButton
                id="cooling-off-period-edit-due-date"
                variant="hollow"
                text={editCoolingOffEndDate ? 'Save' : 'Edit'}
                onClick={saveCoolingOffEndDate}
              />
            )}
          </>
        )}
      </AgxRow>
      {!hideDeleteButton && (
        <AgxRow
          gap="spacing-md"
          fill
          padding={{ top: 'spacing-3xl' }}
          alignItems="flex-end"
          justifyContent="right"
        >
          <AgxColumn gap="spacing-md" fill></AgxColumn>
          <AgxButton
            id="cooling-off-period-delete"
            variant="link"
            danger
            text="Delete"
            onClick={onDelete}
          />
        </AgxRow>
      )}
    </SubPagePanel>
  )
}
